import { useContext, useEffect, useMemo, useRef } from "react";
import { useConnectedEquipment } from "../contexts/ConnectedEquipmentContext";
import { WorkoutContext } from "../contexts/WorkoutContext";
import { getSocket } from "../services/socket";
import { heartRateBasis } from "../utils/heartRateZones";
import { buildMetricsReport } from "../utils/timerMetrics";

const REPORT_MS = 1000;
// A reading older than this is a sensor that has gone quiet; the timer dims
// its readout on its own when reports stop.
const FRESH_MS = 3000;

/**
 * Sends strap HR and bike/rower power to the Khronos Timer once a second
 * while either is connected (KhronosFirmware HR faces). The server forwards
 * it to the right timer: the session's in Live Mode, otherwise your own.
 * Mounted once, in App.
 */
export default function useTimerMetricsRelay() {
  const equipment = useConnectedEquipment();
  const { userProfile } = useContext(WorkoutContext);
  const hrBasis = useMemo(() => heartRateBasis(userProfile), [userProfile]);

  const latest = useRef({ equipment, hrBasis });
  latest.current = { equipment, hrBasis };

  const hrConnected = equipment.hr?.status === "connected";
  const powerConnected = equipment.active?.status === "connected";
  const anyConnected = hrConnected || powerConnected;

  useEffect(() => {
    if (!anyConnected) return undefined;
    const id = setInterval(() => {
      const socket = getSocket();
      if (!socket?.connected) return;
      const { equipment: eq, hrBasis: basis } = latest.current;
      const hrSnap = eq.hr?.getFreshSnapshot?.(FRESH_MS);
      const powerSnap = eq.active?.getFreshSnapshot?.(FRESH_MS);
      const report = buildMetricsReport({
        bpm: hrSnap?.bpm ?? null,
        watts: powerSnap?.power_watts ?? null,
        hrBasis: basis,
      });
      if (report) socket.emit("metrics:report", report);
    }, REPORT_MS);
    return () => clearInterval(id);
  }, [anyConnected]);
}
